import type { AnalyticsEvent, AnalyticsEventName } from '@inboxkit/contracts';
import { API_URL } from './env';

// In-house, cookieless analytics. The Worker hashes IP + user agent with a
// daily salt, so nothing is stored in the browser.

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/** UTM parameters on the current URL. */
export function currentUtm(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, 200);
  }
  return utm;
}

/** Same-site link that keeps the current UTM parameters. */
export function withUtm(path: string): string {
  const query = new URLSearchParams(currentUtm()).toString();
  return query ? `${path}?${query}` : path;
}

/** External referrer only; internal navigation is not a traffic source. */
export function externalReferrer(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const ref = new URL(document.referrer);
    return ref.host === window.location.host ? undefined : document.referrer.slice(0, 1000);
  } catch {
    return undefined;
  }
}

export function track(name: AnalyticsEventName, options: { label?: string } = {}): void {
  const { utm_source, utm_medium, utm_campaign } = currentUtm();
  const referrer = externalReferrer();
  const event: AnalyticsEvent = {
    name,
    path: window.location.pathname.slice(0, 500) || '/',
    ...(referrer ? { referrer } : {}),
    ...(options.label ? { label: options.label } : {}),
    ...(utm_source ? { utm_source } : {}),
    ...(utm_medium ? { utm_medium } : {}),
    ...(utm_campaign ? { utm_campaign } : {}),
  };
  const url = `${API_URL}/v1/events`;
  // text/plain is a CORS "simple" request: no preflight.
  const body = new Blob([JSON.stringify(event)], { type: 'text/plain' });
  try {
    if (navigator.sendBeacon?.(url, body)) return;
  } catch {
    // fall through to fetch
  }
  fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
}
